/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IPanelProps, IPanelStyleProps, IPanelStyles, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { Stack } from '@fluentui/react/lib/Stack';
import { IStyleFunctionOrObject } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { Blade } from 'src/core/components/Blade';
import { BladeModel } from 'src/core/components/BladeModel';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type StackBladeHostProps = Pick<IPanelProps, 'isBlocking' | 'isLightDismiss' | 'isOpen' | 'onDismiss' | 'type'> & {
    /**
     * List of blades.
     */
    blades?: readonly BladeModel[];

    /**
     * Spacing on the top.
     */
    topSpacing?: string;

    /**
     * Callback for when last blade is dismissed.
     */
    onLastBladeDismissed?: (allDismissed?: boolean) => void;
};

const bladeHostTheme = getScopedTheme('StackBladeHost');

/**
 * A host of multiple blades. If there are multiple
 * blades in the same host, then they are stacked
 * horizontally next to each other.
 */
export const StackBladeHost = (props: StackBladeHostProps) => {
    const {
        onDismiss,
        type = PanelType.custom,
        blades = [],
        isOpen = false,
        isBlocking = false,
        onLastBladeDismissed,
        isLightDismiss = true,
        topSpacing = bladeHostTheme.defaultTopSpacing
    } = props;

    const panelStyles: IStyleFunctionOrObject<IPanelStyleProps, IPanelStyles> = {
        main: { top: topSpacing, transition: 'width 250ms step-start', overflowY: 'hidden' },
        content: {
            height: '100%',
            paddingLeft: bladeHostTheme.panelPadding,
            paddingRight: bladeHostTheme.panelPadding,
            paddingBottom: bladeHostTheme.panelPadding,
            paddingTop: bladeHostTheme.panelPadding
        },
        scrollableContent: { height: '100%', overflowY: 'hidden' },
        commands: { margin: 0, paddingTop: 0 }
    };

    /**
     * Renders the given blade information to a new blade component.
     *
     * @param bladeData The data to use to render the blade.
     * @param index The index of the blade.
     * @returns The new blade component.
     */
    const renderBlade = (bladeData: BladeModel, index: number): React.ReactNode => {
        const { key, header, content, fullWidth, width = bladeHostTheme.defaultBladeWidth, className, isDarkTheme } = bladeData;
        const isLastBlade = index === blades.length - 1;

        return (
            <Blade
                key={key}
                width={width}
                header={header}
                content={content}
                activitySource={key}
                className={className}
                fullWidth={fullWidth}
                isDarkTheme={isDarkTheme}
                hasCloseButton={isLastBlade}
                onDismiss={isLastBlade ? () => onLastBladeDismissed(blades.length === 1) : null}
            />
        );
    };

    // Calculates the panel width as a summation of the current blade widths.
    const panelWidth = `${blades.reduce((totalWidth, blade) => totalWidth + (blade.width || bladeHostTheme.defaultBladeWidth), 0)}px`;

    return (
        <Panel
            type={type}
            isOpen={isOpen}
            styles={panelStyles}
            onDismiss={onDismiss}
            hasCloseButton={false}
            isBlocking={isBlocking}
            customWidth={panelWidth}
            isLightDismiss={isLightDismiss}>
            <Stack verticalFill horizontal>
                {blades.map(renderBlade)}
            </Stack>
        </Panel>
    );
};
